import {isEmpty} from '@shared/helpers/lang'

import type {Extra} from './interfaces'

/**
 * Naver Tag Manager 스크립트 HTML 생성
 * Next.js _document.tsx에서 dangerouslySetInnerHTML로 사용
 */
export function getNaverTagManagerScript(config: {domain: string; version: string; envSuffix?: string}): string {
    const {domain, version, envSuffix = ''} = config

    return `
        !(function (t, n) {
            n = n || 'ntm'
            ;((window['ntm_' + t] = n), (window[n] = window[n] || []), window[n].push({'ntm.start': +new Date()}))
            ;((t = document.getElementsByTagName('script')[0]), (n = document.createElement('script')))
            ;((n.async = !0),
                (n.src =
                    'https://${domain}/scripts/ntm_${version}${envSuffix}.js'),
                t.parentNode.insertBefore(n, t))
        })('${version}', 'ntm')
    `
}

export type InitializeParams = {
    /** 이벤트 area 정보 */
    area: string
    /** evts.npay 에 포함될 부가 증적 인자 */
    extra?: Extra
    /** 네이버 서비스 코드 */
    nscode: string
    /**
     * STMS에 등록된 서비스 아이디
     * @see https://stms.navercorp.com/
     */
    stmsID?: string
}
/**
 * NTM 설정값을 저장합니다.
 */
export const initialize = ({nscode, area, stmsID, extra: npay}: InitializeParams) => {
    window['logger'] = {
        area,
        npay,
        nscode,
        stmsID,
    }
}

/**
 * NTM 모듈의 활성화 여부
 * - ntm 모듈이 활성화 된 경우 nclicks, lcs 등은 동작하지 않아야 합니다.
 */
export const isActivated = () => !isEmpty(window['logger'])

type ClickEvent = {
    click_nsc: NaverServiceCode

    click_area: NTMClickArea
    npay?: NTMQuery

    page_nsc?: never

    page_sti?: never
}

type PageViewEvent = {
    click_nsc?: never

    click_area?: never
    npay?: NTMQuery

    page_nsc: NaverServiceCode

    page_sti?: string
}

/**
 * window.ntm wrapper
 */
const callNTM = (params: {code?: string; eventType: NTMEvent; npay?: NTMQuery; sti?: string}) => {
    if (window['logger']) {
        const nlogEvtMap: Record<NTMEvent, Omit<ClickEvent | PageViewEvent, 'npay'>> = {
            click: {
                click_nsc: window['logger'].nscode,

                click_area: `${window['logger'].area}.${params.code}`,
            },
            pageview: {
                page_nsc: window['logger'].nscode,

                page_sti: params.sti,
            },
        }
        window.ntm?.push({
            event: params.eventType,
            nlogEvt: {
                npay: {
                    ...window.logger?.npay,
                    ...params.npay,
                },
                ...nlogEvtMap[params.eventType],
            },
        })
    } else {
        // eslint-disable-next-line no-console
        console.error('Initialize Logger First')
    }
}

type TriggerClickParams = {
    /** window.logger.area 를 기반으로 click area 를 생성하는 경우 */
    code: string
    extra?: NTMQuery
}

/**
 * NTM click event 를 수동으로 trigger 합니다.
 */
export const triggerClick = ({code, extra: npay}: TriggerClickParams) => callNTM({eventType: 'click', code, npay})

/**
 * NTM click event handler 를 간편하게 제공
 */
export const handleNTMClick =
    <T>({clickCode, onClick, data}: {clickCode?: string; onClick?: (data?: T) => void; data?: T}) =>
    (event: React.MouseEvent<HTMLElement>) => {
        event.preventDefault()
        const target = event.currentTarget as HTMLElement
        const code = clickCode ?? target.getAttribute('data-nlog-click-code')
        code && triggerClick({code})
        onClick?.(data)
    }

/**
 * NTM pageview event 를 수동으로 trigger 합니다.
 */
export const triggerPageView = (sti?: string, extra?: Extra) => callNTM({eventType: 'pageview', sti, npay: extra})

/**
 * Nlog 증적 설정 값을 초기화힙니다.
 */
export const cleanup = () => {
    delete window['logger']
}
